<template>
  <!--
    Configuration alerts bell.

    Renders nothing at all when the feature is off for this company or when
    there is nothing to show. An empty or greyed-out bell would quietly claim
    "we are watching and everything is fine", which for a company that has the
    feature switched off is simply untrue.

    See docs/SYSTEM_CONFIG_ALERTS.md §3.6, §11.1
  -->
  <div
    class="dropdown notificationicoblock system-alert-block"
    v-if="enabled && (alerts.length || dismissedCount)"
  >
    <span
      class="notificationico pointer header_icon system-alert-ico"
      :class="severityClass"
      :title="iconTitle"
      data-toggle="dropdown"
      aria-haspopup="true"
      aria-expanded="false"
      aria-label="system alerts"
    >
      <i class="far fa-bell"></i>
      <span class="notification_count" v-if="totalCount > 0">{{ totalCount }}</span>
    </span>

    <ul class="dropdown-menu dropdown-menu-right mt10 system-alert-menu" role="menu">
      <!--
        .stop on the actions: this <li> is inside the dropdown, and without it a
        click would bubble to the toggle and close the menu before the request
        that clears it has been answered.
      -->
      <li class="viewallNotify system-alert-menu-head">
        <a
          href="#"
          class="system-alert-dismiss-all"
          :class="{ 'is-busy': dismissingAll }"
          v-if="alerts.length"
          @click.prevent.stop="dismissAll"
        >{{ dismissingAll ? 'Dismissing…' : 'Dismiss all' }}</a>

        <span class="system-alert-viewall" @click.stop="goToAlerts">View all</span>
      </li>

      <!--
        Everything is dismissed but nothing is fixed. Say so plainly instead of
        leaving an empty menu behind a bell the admin just clicked.
      -->
      <li v-if="!alerts.length" class="system-alert-empty">
        <div class="notificationClick">
          <h6 class="fs-14 bold m-0 lh_18">Nothing needs attention right now</h6>
          <small class="opacity-75 block">
            {{ dismissedCount }} dismissed issue(s) are still unfixed and will
            come back tomorrow.
          </small>
        </div>
      </li>

      <li v-for="alert in alerts" :key="alert.id">
        <div class="notificationClick system-alert-row">
          <span class="system-alert-dot" :class="'sev-' + alert.severity"></span>

          <div class="system-alert-body">
            <h6 class="fs-14 bold m-0 lh_18">{{ alert.title }}</h6>
            <small class="opacity-75 block" v-if="alert.impact">{{ alert.impact }}</small>
            <small class="opacity-50 block">{{ alert.detected_at }}</small>

            <div class="system-alert-actions">
              <a
                href="#"
                class="a_color"
                v-if="alert.action_route"
                @click.prevent="goToFix(alert)"
              ><!-- Normally the server's own wording ("Open Email Settings").
                     The fallback says the same thing as the alerts page, since
                     both links only navigate to a settings screen. -->{{ alert.action_text || 'Open settings' }}</a>
              <a href="#" class="a_color ml10" @click.prevent="dismiss(alert)">Dismiss</a>
            </div>
          </div>
        </div>
      </li>
    </ul>
  </div>
</template>

<script>
export default {
  name: 'SystemAlertBell',

  data () {
    return {
      enabled: false,
      alerts: [],
      criticalCount: 0,
      warningCount: 0,
      totalCount: 0,
      // Unfixed problems the admin has dismissed. Keeps the bell on screen in a
      // muted colour once the list itself is empty.
      dismissedCount: 0,
      pollTimer: null,
      onVisibilityChange: null,
      dismissingAll: false,
      // In-flight lock for the summary, and a note that somebody asked while it
      // was held. See fetchSummary().
      fetchingSummary: false,
      summaryStale: false
    }
  },

  computed: {
    hasCritical () {
      return this.criticalCount > 0
    },

    /*
      Three states, worst first:
        has-critical  red    — something is broken now
        has-warning   orange — something needs attention now
        is-dismissed  grey   — still broken, but the admin has parked it
      The grey one is why the bell no longer disappears on dismiss: an icon that
      vanishes says "fixed", and a dismissal fixes nothing.
    */
    severityClass () {
      if (!this.alerts.length) {
        return 'is-dismissed'
      }
      return this.hasCritical ? 'has-critical' : 'has-warning'
    },

    iconTitle () {
      if (!this.alerts.length) {
        return this.dismissedCount + ' dismissed issue(s), still unfixed'
      }
      if (this.criticalCount > 0) {
        return this.criticalCount + ' critical configuration issue(s)'
      }
      return this.warningCount + ' configuration warning(s)'
    }
  },

  mounted () {
    this.fetchSummary()
    this.startPolling()

    // The navbar is mounted once and kept alive for the whole session (main.vue
    // uses v-show, not v-if), so without this the timer would keep firing in
    // every background tab the admin ever left open. Nothing changes while
    // nobody is looking, so stop; on return, refresh straight away rather than
    // showing a minutes-old count until the next tick.
    this.onVisibilityChange = () => {
      if (document.hidden) {
        this.stopPolling()
      } else {
        this.fetchSummary()
        this.startPolling()
      }
    }

    document.addEventListener('visibilitychange', this.onVisibilityChange)

    // Saving a setting makes the server re-check it, so the bell has to ask
    // again rather than sit on a five-minute-old answer. Raised centrally by
    // the http interceptor, so every settings screen gets this for free.
    this.$bus.$on('system-alerts-refresh', this.fetchSummary)
  },

  beforeDestroy () {
    this.stopPolling()
    this.$bus.$off('system-alerts-refresh', this.fetchSummary)

    if (this.onVisibilityChange) {
      document.removeEventListener('visibilitychange', this.onVisibilityChange)
      this.onVisibilityChange = null
    }
  },

  methods: {
    // Poll rather than push — there is no websocket layer in this app. Five
    // minutes because the checks behind this only run on a 15-minute cron, so
    // anything faster re-asks a question whose answer cannot have changed.
    // Actions the user takes (dismiss, recheck) refresh immediately regardless.
    startPolling () {
      this.stopPolling()
      this.pollTimer = setInterval(this.fetchSummary, 300000)
    },

    stopPolling () {
      if (this.pollTimer) {
        clearInterval(this.pollTimer)
        this.pollTimer = null
      }
    },

    /*
     * One request at a time.
     *
     * The five-minute poll, the tab regaining focus, the refresh raised after a
     * save and the user's own dismiss can all land together, and each extra one
     * asks a question that is already in flight.
     *
     * Anything that arrives mid-request is remembered rather than dropped: the
     * reply now on its way was sent before whatever prompted the new request,
     * so it cannot be trusted to contain it. One follow-up runs when the
     * current one lands, however many callers piled up behind it.
     */
    fetchSummary () {
      if (this.fetchingSummary) {
        this.summaryStale = true
        return
      }

      this.fetchingSummary = true
      this.summaryStale = false

      // hideLoader: the global progress bar is for things the user asked for.
      // Without it this would flash across the top of the screen every minute.
      this.$http
        .get('/system-alerts/summary', { hideLoader: true })
        .then((response) => {
          // Released in both branches rather than a .finally(), which this
          // build does not polyfill — and a flag left set would stop the bell
          // ever refreshing again.
          this.releaseSummary()

          const meta = (response.data && response.data.meta) || {}

          this.enabled = !!meta.enabled
          this.alerts = (response.data && response.data.data) || []
          this.criticalCount = meta.criticalCount || 0
          this.warningCount = meta.warningCount || 0
          this.totalCount = meta.totalCount || 0
          this.dismissedCount = meta.dismissedCount || 0

          // The banner lives in the page shell, so hand it the payload we
          // already fetched instead of making it poll separately. It arrives
          // grouped by area — { total, types: [{ key, label, count, route }] }.
          this.$bus.$emit('system-alerts-updated', {
            enabled: this.enabled,
            banner: meta.banner || { total: 0, types: [] }
          })
        })
        .catch(() => {
          this.releaseSummary()

          // A failed poll is not worth a toast — the next one is 5 minutes away.
          this.enabled = false
          this.alerts = []
          this.dismissedCount = 0
        })
    },

    /** Free the in-flight lock, and serve whoever asked while it was held. */
    releaseSummary () {
      this.fetchingSummary = false

      if (this.summaryStale) {
        this.summaryStale = false
        this.fetchSummary()
      }
    },

    goToAlerts () {
      this.$router.push({ name: 'systemAlerts' }).catch(() => {})
    },

    goToFix (alert) {
      if (!alert.action_route) {
        return
      }

      this.$router
        .push({ name: alert.action_route, params: alert.action_params || {} })
        .catch(() => {
          // Some settings screens are permission-gated; falling back to the
          // alerts page is friendlier than a silent no-op.
          this.goToAlerts()
        })
    },

    dismiss (alert) {
      this.$http
        .post('/system-alerts/' + alert.id + '/dismiss')
        .then(() => this.fetchSummary())
        .catch(() => {})
    },

    /*
     * Clear the bell in one go.
     *
     * The server dismisses every visible alert, not just the five listed here,
     * so the badge and this list end up agreeing. Guarded against a double
     * click: a second request would find nothing left and report "0 dismissed".
     */
    dismissAll () {
      if (this.dismissingAll) {
        return
      }

      this.dismissingAll = true

      this.$http
        .post('/system-alerts/dismiss-all')
        .then(() => this.fetchSummary())
        .catch(() => {})
        .then(() => {
          this.dismissingAll = false
        })
    }
  }
}
</script>

<style scoped>
.system-alert-ico.has-critical i {
  color: #c0392b;
}

.system-alert-ico.has-warning i {
  color: #e67e22;
}

/* Dismissed but not fixed. Muted rather than hidden — still findable, no longer
   demanding. Deliberately not green: nothing here has been put right. */
.system-alert-ico.is-dismissed i {
  color: #9aa5b1;
}

.system-alert-menu {
  min-width: 340px;
  max-width: 400px;
}

/* Two actions on one line: clearing the bell on the left, the full list on the
   right, so the destructive-looking one is not where the eye lands last. */
.system-alert-menu-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.system-alert-dismiss-all,
.system-alert-viewall {
  cursor: pointer;
}

.system-alert-dismiss-all.is-busy {
  opacity: 0.6;
  pointer-events: none;
}

.system-alert-row {
  display: flex;
  align-items: flex-start;
}

.system-alert-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  margin: 6px 10px 0 0;
  flex: 0 0 8px;
}

.system-alert-dot.sev-critical {
  background: #c0392b;
}

.system-alert-dot.sev-warning {
  background: #e67e22;
}

.system-alert-dot.sev-info {
  background: #2c7be5;
}

.system-alert-body {
  flex: 1 1 auto;
  min-width: 0;
}

.system-alert-actions {
  margin-top: 4px;
  font-size: 12px;
}
</style>



// WEBPACK FOOTER //
// system_alert_bell.vue?4d58d004